import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'

interface UseTeamSnapshotStatsOptions {
  teamResID?: string
  enabled?: boolean // Whether to fetch data (default: true)
}

interface SnapshotStatsData {
  usageBytes: number
  quotaBytes: number
  count: number
  slotQuota: number
}

/**
 * Hook to fetch team snapshot statistics
 * Uses the unified /team/stat/snapshots endpoint to get both team shared
 * and all members' snapshot data in a single API call
 *
 * Replaces useTeamSnapshotQuota which used MOCK data and multiple API calls
 *
 * @param options.teamResID - Team resource ID
 * @param options.enabled - Whether to fetch data (default: true). Use this to conditionally
 *                          fetch based on permissions or other business logic.
 */
export function useTeamSnapshotStats(options: UseTeamSnapshotStatsOptions) {
  const { teamResID, enabled = true } = options

  const [teamShared, setTeamShared] = useState<SnapshotStatsData>({
    usageBytes: 0,
    quotaBytes: 0,
    count: 0,
    slotQuota: 0,
  })

  const [allMembers, setAllMembers] = useState<SnapshotStatsData>({
    usageBytes: 0,
    quotaBytes: 0,
    count: 0,
    slotQuota: 0,
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchStats = useCallback(async () => {
    // Don't fetch if disabled or no team selected
    if (!enabled || !teamResID) {
      // Reset all states when disabled or no team is selected
      setTeamShared({ usageBytes: 0, quotaBytes: 0, count: 0, slotQuota: 0 })
      setAllMembers({ usageBytes: 0, quotaBytes: 0, count: 0, slotQuota: 0 })
      return
    }

    setLoading(true)
    setError(null)

    try {
      const res = await API.getTeamStatSnapshots({ res_id: teamResID })

      // Team shared snapshot stats
      setTeamShared({
        usageBytes: res.teamSharedSnapshotSpaceOverview.usageBytes,
        quotaBytes: res.teamSharedSnapshotSpaceOverview.quotaBytes,
        count: res.teamSharedSnapshotOverview.usage,
        slotQuota: res.teamSharedSnapshotOverview.quota,
      })

      // All members aggregate snapshot stats
      setAllMembers({
        usageBytes: res.memberSnapshotSpaceOverview.usageBytes,
        quotaBytes: res.memberSnapshotSpaceOverview.quotaBytes,
        count: res.memberSnapshotOverview.usage,
        slotQuota: res.memberSnapshotOverview.quota,
      })
    } catch (err) {
      console.error('Failed to fetch team snapshot stats:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [enabled, teamResID])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    // Team shared snapshot data
    teamShared,
    // All members aggregate data
    allMembers,
    loading,
    error,
    refetch: fetchStats,
  }
}
